import * as indexedDBFunctions from '../../utils/indexedDBFunctions';
import { getUuidv4 } from '../image/shapes/ShapeFunctions';

/* OFFLINE UPDATE GUIDE
====================

1. IMPLEMENT UI TOGGLE 
  - In component handlers, check offline status and dispatch appropriate action:

  const handleButtonClick = () => {
    if (isOnline) {
      dispatch(updateNode({ documentId, nodeId, value }))
    } else {
      dispatch(updateNodeOffline({ documentId, nodeId, payload: { value } }))
    }
  }

2. UPDATE STATE HANDLERS
  - In documentSlice.js handleNodeFulfilledOffline function
  - If you need to update the state with the task data, add a case to update Redux state:

  if (node.type === 'your-node-type') {
    node.title = taskData.title;
  }

3. ADD STORAGE HANDLERS
  - In offlineFunctions.js, update updateNodeWithTaskData to handle your node in indexedDB:
  - Same as above, documentSlice.js handleNodeFulfilledOffline

  if (type === 'your-node-type') {
    node.title = taskData.title;
  }

4. CONFIGURE SYNC MAPPING
  - In syncActions.js, map offline tasks to their online counterparts:

  'your-node-type': {
    action-type: (payload) => ({
      action: documentSlice.updateNode,
      params: {
        documentId: payload.documentId,
        nodeId: payload.nodeId,
        value: payload.payload.value
      }
    })
  }

5. By default, previous task with the same documentId, nodeId and actionType will be overwritten in pendingTasks.
  For special cases, you can add a custom logic in findExistingTask function in offlineFunctions.js.
  For example: Avoid overwriting existing tasks or merge task data between updates (as for table-row).
*/

// Handle save, update, delete and move actions for document nodes in offline mode
export const handleOfflineNodeSave = async (stateData, taskPayload, nodeId) => {
  if (nodeId == null) {
    console.error('Node ID not found in handleOfflineNodeSave');
    return;
  }

  let documentData = JSON.parse(JSON.stringify(stateData));
  const {
    documentId,
    objectId,
    userId,
    actionType,
    taskData = null, // payload for backend call
    tempNode = null, // new temporary node
    parentNodeId = null // when deleting a node
  } = taskPayload;

  const { node, type } = getNodeAndType(documentData, nodeId, parentNodeId, actionType, taskData);
  if (!node) {
    console.error('Node not found in document data');
    return;
  }

  // temporary id for nodes created offline
  const tempId = nodeId && nodeId.startsWith('temp-') ? nodeId : null;
  const timestamp = Math.floor(Date.now() / 1000);

  const task = {
    nodeId: node.id,
    documentId: documentId,
    objectId: objectId,
    context: "document",
    type: type,
    actionType: actionType, // add, update, delete, move
    payload: taskData,
    tempId: tempId,
    parentNodeId: parentNodeId,
    key: node.key || null,
    userId: userId,
    timestamp: timestamp,
  }

  // Update documentData.id as it is in original backend data
  documentData.id = documentId;

  if (actionType === 'add' && node.type === 'macro-section') {
    // Add key node mapping to task to help update section nodes on server response
    const childNodeIdMapping = mapKeysToIds(tempNode);
    task.childNodeIdMapping = childNodeIdMapping;
  }

  try {
    let addTask = true;
    // Update documentData based on actionType
    switch (actionType) {
      case 'add':
        await handleAddAction(node, tempNode, task, userId);
        break;
      case 'delete':
        addTask = await handleDeleteAction(documentData, node, parentNodeId, documentId, userId);
        break;
      case 'update':
        await handleUpdateAction(node, taskData, task, userId);
        break;
      case 'move':
        handleMoveAction(node, taskData);
        break;
      default:
        console.error(`Invalid actionType in: ${actionType}`);
        return;
    }

    if (addTask) {
      await indexedDBFunctions.addDataToDB(userId, 'pendingTasks', task);
    }
    await indexedDBFunctions.addDataToDB(userId, 'documents', documentData);
  } catch (error) {
    console.error("Error handling offline save:", error);
  }
}

const getNodeAndType = (documentData, nodeId, parentNodeId, actionType, taskData) => {
  // Separate handling for top-level section move
  if (actionType === 'move' && taskData.isSubSection === false && !parentNodeId) {
    return { node: { id: nodeId, nodes: documentData.nodes }, type: 'section' };
  }
  const node = recursiveFindNode(documentData, nodeId);
  return { node, type: node?.type };
};

// Handle saving root of the document data (not document nodes) to indexedDB
export const handleOfflineDocumentDataSave = async (stateData, taskPayload) => {
  let documentData = JSON.parse(JSON.stringify(stateData));
  const {
    documentId,
    objectId,
    userId,
    actionType,
    type,
    taskData = null, // payload for backend call
  } = taskPayload;

  const timestamp = Math.floor(Date.now() / 1000);

  const task = {
    nodeId: "",
    documentId: documentId,
    objectId: objectId,
    context: "document",
    type: type,
    actionType: actionType,
    payload: taskData,
    userId: userId,
    timestamp: timestamp,
  }

  // List of updateable fields
  const updateableFields = ['date_ts'];

  // Update documentData with taskData
  if (taskData) {
    Object.keys(taskData).forEach(key => {
      if (updateableFields.includes(key)) {
        documentData[key] = taskData[key];
      }
    });
  }

  documentData.id = documentId;
  try {
    const pendingTasks = await indexedDBFunctions.getAllDataFromDB(userId, 'pendingTasks');

    // Check if there is already a task by comparing the payload, documentId and actionType
    const existingTask = pendingTasks.find(t =>
      t.documentId === documentId &&
      t.context === "document" &&
      t.actionType === actionType &&
      Object.keys(taskData).every(key => key in t.payload)
    );

    if (existingTask) {
      // Update the taskId of the new task to match the existing task
      task.taskId = existingTask.taskId;
    }

    await indexedDBFunctions.addDataToDB(userId, 'pendingTasks', task);
    await indexedDBFunctions.addDataToDB(userId, 'documents', documentData);
  } catch (error) {
    console.error("Error handling offline save:", error);
  }
}

const handleAddAction = async (node, tempNode, task, userId) => {
  if (tempNode) {
    // Store the temporary node id in the task
    task.tempId = tempNode.id;
    // Add the new node to the documentData
    node.nodes.push(tempNode);

    // Store image data to documentImages store
    if (tempNode.type === 'image' && task.payload.data) {
      await indexedDBFunctions.addDataToDB(userId, 'documentImages', {
        documentId: task.documentId,
        nodeId: tempNode.id,
        data: task.payload.data,
      });
    }
  }
};

const handleDeleteAction = async (documentData, node, parentNodeId, documentId, userId) => {
  if (node.type === 'image') {
    await indexedDBFunctions.deleteDataByCriteria(userId, 'documentImages', { documentId, nodeId: node.id });
  }

  if (node.type === 'section') {
    await deleteSectionTasks(userId, documentId, node);
  }

  if (node.id.startsWith('temp-')) {
    await deleteNodeCreatedOffline(documentData, node, parentNodeId, documentId, userId);
    return false;
  }

  if (parentNodeId) {
    // Remove the node from the documentData
    const parentNode = recursiveFindNode(documentData, parentNodeId);
    parentNode.nodes = parentNode.nodes.filter(n => n.id !== node.id);
  }

  return true;
};

const handleUpdateAction = async (node, taskData, task, userId) => {
  const existingTask = await findExistingTask(userId, task, taskData);

  if (existingTask) {
    // Update the taskId of the new task to match the existing task
    task.taskId = existingTask.taskId;
  }

  updateNodeWithTaskData(node, taskData, node.type);
};

const handleMoveAction = (node, taskData) => {
  const { fromIndex, toIndex } = taskData;

  if (fromIndex < 0 || fromIndex >= node.nodes.length) {
    throw new Error('Invalid fromIndex');
  }

  if (toIndex < 0 || toIndex >= node.nodes.length) {
    throw new Error('Invalid toIndex');
  }

  const [movedNode] = node.nodes.splice(fromIndex, 1);
  node.nodes.splice(toIndex, 0, movedNode);
};

const deleteSectionTasks = async (userId, documentId, node) => {
  // Delete all tasks related to the section
  const nodeIds = [];
  const collectNodeIds = (node) => {
    if (node === null) return;
    nodeIds.push(node.id);
    if (node.nodes) {
      node.nodes.forEach(n => collectNodeIds(n));
    }
  };
  collectNodeIds(node);

  // Get all pendingTasks for the document and delete the ones related to the section
  const pendingTasks = await indexedDBFunctions.getTasksByCriteria(userId, { documentId });
  const sectionTasks = pendingTasks.filter(t => nodeIds.includes(t.nodeId));
  for (let task of sectionTasks) {
    await indexedDBFunctions.deleteDataFromDB(userId, 'pendingTasks', task.taskId);
  }
};

// Checks if there's an existing task that should be updated rather than creating a new task
// By default it will overwrite the existing task with same documentId, nodeId and actionType
const findExistingTask = async (userId, task, taskData) => {
  let searchCriteria = {
    documentId: task.documentId,
    nodeId: task.nodeId,
    actionType: task.actionType,
  };

  if (task.type === 'section') {
    let pendingTasks = await indexedDBFunctions.getTasksByCriteria(userId, searchCriteria);
    
    // Only update existing tasks if we're modifying the same property
    // (e.g. don't overwrite a title change with a pageBreak change)
    return pendingTasks.find(t =>
      (('title' in taskData && 'title' in t.payload) || ('pageBreak' in taskData && 'pageBreak' in t.payload))
    );
  } else if (task.type === 'table-row') {
    const existingTask = await indexedDBFunctions.getTaskByCriteria(userId, searchCriteria);
  
    if (existingTask) {
      // Merge the payloads - keep old properties that aren't in the new taskData
      task.payload = { ...existingTask.payload, ...taskData };
      return existingTask;
    }
    
    // If no existing task, create a new one
    return false;
  } else {
    return await indexedDBFunctions.getTaskByCriteria(userId, searchCriteria);
  }

};

// Same as in documentSlice.js handleNodeFulfilledOffline
// Update node in indexedDB with taskData based on the type of node
const updateNodeWithTaskData = (node, taskData, type) => {
  if (type === 'table-row') {
    if (taskData.values) {
      node.values = taskData.values;
    }
    if (taskData.styles) {
      node.styles = taskData.styles;
    }
  } else if (type === 'note' || type === 'image' || type === 'file') {
    for (const key in taskData) {
      node[key] = taskData[key];
    }
  } else if (type === 'input-signature' && taskData.userId) {
    node.signingRightsId = taskData.userId;
    if (taskData.userId === -1) {
      node.value = null;
    }
  } else if (type === 'section' && 'pageBreak' in taskData) {
    node.pageBreak = taskData.pageBreak === 1 ? 'pageBreakBefore' : null;
  } else if (node.type === 'section' && 'title' in taskData) {
    node.title = taskData.title;
  } else {
    node.value = { ...node.value || {}, ...taskData };
  }
};

const deleteNodeCreatedOffline = async (documentData, node, parentNodeId, documentId, userId) => {
  const criteria = { documentId, tempId: node.id };
  // Delete all tasks matching the criteria
  await indexedDBFunctions.deleteTasksByCriteria(userId, criteria);

  if (parentNodeId) {
    // Remove the node from the documentData
    const parentNode = recursiveFindNode(documentData, parentNodeId);
    parentNode.nodes = parentNode.nodes.filter(n => n.id !== node.id);
  }
};

export const recursiveFindNode = (data, nodeId) => {
  if (!data) return null;

  if (data.id === nodeId) {
    return data;
  }

  if (data.nodes) {
    for (let node of data.nodes) {
      if (node === null) continue;
      const foundNode = recursiveFindNode(node, nodeId);
      if (foundNode) {
        return foundNode;
      }
    }
  }

  return null;
};

// Generate temporary id
export const generateTempId = () => {
  return 'temp-' + getUuidv4();
}

function mapKeysToIds(data) {
  const result = {};

  function traverseNodes(nodes) {
    nodes.forEach(node => {
      if (node.key && node.id) {
        result[node.id] = node.key; // Map key to id
      }
      if (node.nodes && node.nodes.length > 0) {
        traverseNodes(node.nodes); // Recurse if there are nested nodes
      }
    });
  }

  if (data.nodes) {
    traverseNodes(data.nodes);
  }

  return result;
}